/**
 * Request-scoped server context for SSR data fetchers.
 *
 * Carries the *incoming* request's `Cookie` header + the verified
 * userId so ad-hoc server-side helpers (`serverGraphqlFetch`,
 * `serverGraphqlFetchRaw`, prewarm helpers, …) can call upstream
 * GraphQL **on behalf of the current user** without each helper
 * having to thread the React Router `context` argument through
 * its signature.
 *
 * Why: before this existed, every SSR GraphQL fetch ran as a fully
 * anonymous client (`fetch('/graphql')` with no Cookie/Authorization
 * header). That meant a logged-in owner viewing their own private /
 * training-in-progress LoRA at `/model/:id` saw the SSR error page
 * because anonymous GraphQL returned `null` for a model only the
 * owner is allowed to read — even though the *client-side* Apollo
 * client could fetch it perfectly fine using the user's
 * `user_token` cookie. The fix is to forward the same cookie SSR
 * already received from the browser to the upstream GraphQL call.
 *
 * Mirrors `server-timing-als.server.ts`'s shape so the pattern is
 * recognisable across the codebase.
 *
 * Wiring contract (see `auth.middleware.server.ts`):
 *   - The middleware reads the `cookie` header off `request.headers`
 *     and verifies the JWT; whatever it learns becomes the value
 *     stored in this ALS for the rest of the handler chain.
 *   - All loaders / deferred promises / GraphQL helpers that run
 *     during the same request can read it via
 *     `getCurrentRequestServerContext()`.
 *   - Code paths that run *outside* a request (background prewarm,
 *     cron-style helpers) get `null` and behave anonymously, exactly
 *     like before this module existed.
 */
import { AsyncLocalStorage } from 'node:async_hooks'

export interface RequestServerContext {
  /**
   * Verbatim copy of the incoming request's `Cookie` header. Empty
   * string when the request had no cookies at all. Forwarded
   * unchanged on upstream GraphQL calls so the API server sees the
   * same `user_token` JWT (and any other cookies) the browser sent.
   */
  cookieHeader: string
  /**
   * Verified userId (`sub` claim from the `user_token` JWT) when the
   * request was authenticated, otherwise `undefined`. Currently used
   * for diagnostics and as a hint to skip the disk-backed SSR
   * GraphQL cache for authenticated requests (so user A's private
   * data can never be served to user B from the cache).
   */
  userId: string | undefined
}

const requestContextALS = new AsyncLocalStorage<RequestServerContext>()

export function runWithRequestServerContext<T>(
  ctx: RequestServerContext,
  fn: () => T,
): T {
  return requestContextALS.run(ctx, fn)
}

/**
 * Returns the request-scoped server context, or `null` when called
 * outside the request handler chain (e.g. cached background prewarm
 * fetches that don't belong to a specific incoming request — they
 * intentionally fall back to anonymous behaviour).
 */
export function getCurrentRequestServerContext(): RequestServerContext | null {
  return requestContextALS.getStore() ?? null
}
